import { simpleCopy } from '@/utils/deepCopy';
import { IRecommendOrderArrival } from '@/recommendOrderModule/domains/entities/recommendOrderArrival';
import {
  IRecommendOrderArrivalForRecommendEdit,
  arrivalForRecommendEditToIRecommendOrderArrival,
} from '../../pages/interfaces/recommendOrder';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { RECOMMENDED_ORDER_DATE_WHEN_NULL } from '@/recommendOrderModule/constants';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IDate } from '@/coreDomainsModule/date';

/**
 * 発注推奨を操作するためのサービス
 */
export class RecommendService {
  /**
   *　recommendの確定発注フラグを更新する
   * @param definedRecommends - 編集用の推奨発注情報
   */
  public static setDefinedOrderFromIRecommendOrderArrivalForRecommendEdit(
    definedRecommends: IRecommendOrderArrivalForRecommendEdit[]
  ): IRecommendOrderArrival[] {
    const output: IRecommendOrderArrival[] = [];
    const definedOrder: IRecommendOrderArrival[] = arrivalForRecommendEditToIRecommendOrderArrival(definedRecommends);
    const approvalStatuses: OrderStatus[] = [OrderStatus.ApprovalRequested, OrderStatus.ApprovedAwaitingDeliveryDate];
    definedOrder.forEach((recommendData) => {
      if (approvalStatuses.includes(recommendData.status ?? OrderStatus.Recommended)) {
        // 確定発注フラグの更新
        output.push(simpleCopy({ ...recommendData, isFixOrdered: true }));
      }
    });

    return output;
  }

  /**
   * crossDateの日付順にソートする(デフォルトは昇順)
   * crossDateが存在しない場合は、最後尾に配置される
   * @param recommend　- 編集用の推奨発注情報
   * @param asc　- 昇順かどうか
   */
  public static sortRecommend(recommend: IRecommendOrderArrivalForRecommendEdit[], asc = true): IRecommendOrderArrivalForRecommendEdit[] {
    const output = simpleCopy(recommend);
    output.sort((a, b) => {
      const crossDateA = a?.crossDate
        ? new Date(a.crossDate.year, a.crossDate.month - 1, a.crossDate.day).getTime()
        : Number.MAX_SAFE_INTEGER;
      const crossDateB = b?.crossDate
        ? new Date(b.crossDate.year, b.crossDate.month - 1, b.crossDate.day).getTime()
        : Number.MAX_SAFE_INTEGER;
      return asc ? crossDateA - crossDateB : crossDateB - crossDateA;
    });
    return output;
  }

  /**
   *　recommendの中に推奨発注情報が存在するかを確認する
   *  必要であれば、index配列を渡して特定indexで絞った上でのチェックを行う
   * @param recommend　- 推奨発注情報
   * @param saveRecommendIndexes　- 確認を行うindexの配列
   * @returns　(boolean) 推奨発注情報が含まれているかどうか
   */
  public static findRecommendedOrderFromIRecommendOrderArrivalForRecommendEdit(
    recommend: IRecommendOrderArrivalForRecommendEdit[],
    saveRecommendIndexes?: number[]
  ): boolean {
    const approvalStatuses: OrderStatus[] = [OrderStatus.Recommended];
    if (!saveRecommendIndexes?.length) {
      const filteredRecommend = recommend.filter((orderData) => approvalStatuses.includes(orderData.status ?? OrderStatus.Recommended));
      return filteredRecommend.length > 0;
    }
    const filteredRecommend = recommend.filter(
      (orderData, index) => approvalStatuses.includes(orderData.status ?? OrderStatus.Recommended) && saveRecommendIndexes.includes(index)
    );
    return filteredRecommend.length > 0;
  }

  /**
   * 推奨発注日を計算して返す
   * NOTE: 推奨発注日 = 推奨納期 - リードタイム
   * @param recommend　- 推奨発注情報
   */
  public static calculateRecommendedCreateOrderDate(recommend: IRecommendOrderArrival): IDate {
    if (!recommend.crossDate) {
      // 推奨納期がnullの場合、推奨発注日は初期値を返す
      return {
        year: RECOMMENDED_ORDER_DATE_WHEN_NULL.year,
        month: RECOMMENDED_ORDER_DATE_WHEN_NULL.month,
        day: RECOMMENDED_ORDER_DATE_WHEN_NULL.day,
      };
    }

    // 納期をDate型に変換
    const crossDate = new Date(recommend.crossDate.year, recommend.crossDate.month - 1, recommend.crossDate.day);
    // 発注日を計算(リードタイムは日数)
    const createOrderDate = new Date(crossDate);
    createOrderDate.setDate(crossDate.getDate() - recommend.leadTime);
    const createOrderIDate = DateService.convertDateToIDate(createOrderDate);
    return {
      year: createOrderIDate.year,
      month: createOrderIDate.month,
      day: createOrderIDate.day,
    };
  }
}
